import React, { useState } from 'react';
import { Grid2, Card, CardContent, Typography, Switch, Tooltip, Box } from '@mui/material';

// Reusable MetricCard component
function MetricCard({
    // Basic info
    header,
    subheader,
    gridSize = 3,
    // Values array - each object should have: { label, value, color, valueType }
    values = [],
    // Toggle functionality - object containing all toggle config
    toggle = null // { values: [], subheader: '', defaultState: false, leftIcon: null, rightIcon: null, tooltip: '' }
}) {
    // Toggle state
    const hasToggle = toggle !== null;
    const [isToggled, setIsToggled] = useState(toggle?.defaultState || false);
    
    // Calculate display values based on valueType
    const getDisplayValue = (rawValue, valueType) => {
        if (valueType === 'percent') {
            return `${(rawValue * 100).toFixed(1)}%`;
        } else {
            return rawValue?.toLocaleString ? rawValue.toLocaleString() : (rawValue || 0).toFixed(0);
        }
    };
    
    // Merge values with toggleValues when toggle is active
    const getDisplayValues = () => {
        if (!hasToggle || !isToggled || !toggle.values) {
            return values;
        }
        
        return values.map((originalValue, index) => {
            const toggleValue = toggle.values[index];
            // If no toggle value exists or it's empty object, keep original
            if (!toggleValue || Object.keys(toggleValue).length === 0) {
                return originalValue;
            }
            // Merge toggle value properties with original
            return { ...originalValue, ...toggleValue };
        });
    };
    
    const displayValues = getDisplayValues();
    const displaySubheader = hasToggle && isToggled && toggle?.subheader ? toggle.subheader : subheader;
    const displayHeader = hasToggle && isToggled && toggle?.altHeader ? toggle.altHeader : header;
    
    // Render value boxes based on count
    const renderValueBoxes = () => {
        const count = displayValues.length;
        
        if (count === 0) return null;
        
        const createBox = (valueObj, index) => (
            <Box 
                key={index}
                sx={{ 
                    flex: count === 1 ? 1 : count === 3 && index === 2 ? 1 : '1 1 50%',
                    textAlign: 'center', 
                    p: 1, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1 
                }}
            >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {valueObj.label}
                </Typography>
                <Typography variant="h5" color={valueObj.color || 'primary.main'}>
                    {getDisplayValue(valueObj.value, valueObj.valueType)}
                </Typography>
            </Box>
        );
        
        if (count === 1) {
            return (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {createBox(displayValues[0], 0)}
                </Box>
            );
        } else if (count === 2) {
            return (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {displayValues.map((valueObj, index) => createBox(valueObj, index))}
                </Box>
            );
        } else if (count === 3) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {createBox(displayValues[0], 0)}
                        {createBox(displayValues[1], 1)}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {createBox(displayValues[2], 2)}
                    </Box>
                </Box>
            );
        } else if (count === 4) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {createBox(displayValues[0], 0)}
                        {createBox(displayValues[1], 1)}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {createBox(displayValues[2], 2)}
                        {createBox(displayValues[3], 3)}
                    </Box>
                </Box>
            );
        } else {
            // For 5+ values, just wrap them in rows of 2
            const rows = [];
            for (let i = 0; i < displayValues.length; i += 2) {
                rows.push(displayValues.slice(i, i + 2));
            }
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {rows.map((row, rowIndex) => (
                        <Box key={rowIndex} sx={{ display: 'flex', gap: 2 }}>
                            {row.map((valueObj, index) => createBox(valueObj, rowIndex * 2 + index))}
                        </Box>
                    ))}
                </Box>
            );
        }
    };

    // Create toggle switch if needed
    const renderToggleSwitch = () => {
        if (!hasToggle) return null;
        
        return (
            <Tooltip title={toggle?.tooltip || 'Toggle view'}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {toggle?.leftIcon && (
                        <Box sx={{ fontSize: '1rem', opacity: !isToggled ? 1 : 0.5, mr: 0.5 }}>
                            {toggle.leftIcon}
                        </Box>
                    )}
                    <Switch 
                        checked={isToggled}
                        onChange={(e) => setIsToggled(e.target.checked)}
                        size='small'
                        sx={{ mx: 0.5 }}
                    />
                    {toggle?.rightIcon && (
                        <Box sx={{ fontSize: '1rem', opacity: isToggled ? 1 : 0.5, ml: 0.5 }}>
                            {toggle.rightIcon}
                        </Box>
                    )}
                </Box>
            </Tooltip>
        );
    };

    return (
        <Grid2 size={{ xs: 12, sm: gridSize }} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                            <Typography variant='h6' color='text.secondary'>
                                {displayHeader}
                            </Typography>
                            {renderToggleSwitch()}
                        </div>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                            {displaySubheader}
                        </Typography>
                    </div>
                    {renderValueBoxes()}
                </CardContent>
            </Card>
        </Grid2>
    );
}

export default MetricCard;